'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { Container, Section } from '@/components/primitives';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import { BookCover } from '@/components/book-cover';
import { BookCard } from '@/components/book-card';
import { EmptyState } from '@/components/empty-state';
import { Icon } from '@/components/icons';
import { useAuth } from '@/lib/auth-context';
import { useToast } from '@/lib/toast-context';
import { clientActiveRentals, clientRentalHistory, clientFavorites, clientReturnRental, clientProgressMap } from '@/lib/client-api';
import type { Book, Rental, FavoriteItem } from '@/types/catalog';

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

function rentalToBook(r: Rental): Book {
  return { id: r.bookId, title: r.title, author: r.author, coverUrl: r.coverUrl, pages: r.pages, year: r.year };
}

function favoriteToBook(f: FavoriteItem): Book {
  return { id: f.bookId, title: f.title, author: f.author, coverUrl: f.coverUrl, year: f.year, subject: f.subject };
}

function LoanCard({ loan, progress, onReturned }: { loan: Rental; progress?: number; onReturned: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const now = new Date();
  const expires = new Date(loan.expiresAt);
  const started = new Date(loan.startedAt);
  const remaining = Math.max(0, daysBetween(now, expires));
  const urgent = remaining < 5;

  const onReturn = async () => {
    setBusy(true);
    const ok = await clientReturnRental(loan.id);
    setBusy(false);
    if (ok) {
      toast.show('Título devuelto. ¡Gracias!', 'success');
      onReturned();
    } else {
      toast.show('No pudimos devolver el título. Inténtalo de nuevo.', 'error');
    }
  };

  return (
    <div
      className="m-loan-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr auto',
        gap: 28,
        alignItems: 'center',
        padding: '28px 0',
        borderBottom: '1px solid var(--rule)',
      }}
    >
      <BookCover book={rentalToBook(loan)} w={120} />
      <div>
        <div className="serif" style={{ fontSize: 24, lineHeight: 1.15 }}>{loan.title}</div>
        <div style={{ color: 'var(--stone)', fontSize: 14, marginTop: 4 }}>{loan.author}</div>

        {progress !== undefined && progress > 0 && (
          <div style={{ marginTop: 20, maxWidth: 460 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--stone)', marginBottom: 8 }}>
              <span>Avance de lectura</span>
              <span style={{ fontFamily: 'var(--mono)' }}>{progress}%</span>
            </div>
            <div className="progress"><i style={{ width: `${progress}%` }} /></div>
          </div>
        )}

        <div style={{ marginTop: 18, display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: urgent ? 'var(--accent)' : 'var(--ink-soft)',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: urgent ? 'var(--accent)' : 'var(--ink)',
              }}
            />
            {remaining > 0 ? `${remaining} días restantes` : 'Vence hoy'}
          </span>
          <span style={{ color: 'var(--stone)' }}>·</span>
          <span style={{ color: 'var(--stone)' }}>
            Tomado el {started.toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch', minWidth: 200 }}>
        <Link
          href={`${loan.product === 'audiobook' ? '/escuchar' : '/lectura'}?bookId=${loan.bookId}`}
          className="btn btn-primary"
          style={{ justifyContent: 'center' }}
        >
          <Icon.book /> {loan.product === 'audiobook' ? 'Continuar escuchando' : 'Continuar leyendo'}
        </Link>
        <button className="btn btn-ghost" style={{ justifyContent: 'center' }} disabled={busy} onClick={onReturn}>
          {busy ? 'Devolviendo…' : 'Devolver'}
        </button>
      </div>
    </div>
  );
}

function HistoryRow({ entry }: { entry: Rental }) {
  const date = new Date(entry.returnedAt ?? entry.expiresAt).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return (
    <div
      className="m-row-tight m-wrap"
      style={{
        display: 'grid',
        gridTemplateColumns: '64px 1fr auto auto',
        gap: 22,
        alignItems: 'center',
        padding: '18px 0',
        borderBottom: '1px solid var(--rule)',
      }}
    >
      <BookCover book={rentalToBook(entry)} w={64} />
      <div>
        <div className="serif" style={{ fontSize: 17 }}>{entry.title}</div>
        <div style={{ color: 'var(--stone)', fontSize: 13 }}>{entry.author}</div>
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)', textAlign: 'right' }}>
        {entry.returnedAt ? `Devuelto el ${date}` : `Venció el ${date}`}
      </div>
      <Link href={`/libro/${entry.bookId}`} className="btn btn-ghost" style={{ padding: '8px 14px', fontSize: 13 }}>
        Ver título
      </Link>
    </div>
  );
}

export default function ShelfPage() {
  const { loggedIn, hydrating, user } = useAuth();
  const [tab, setTab] = useState<'activos' | 'historial' | 'favoritos'>('activos');
  const [active, setActive] = useState<Rental[]>([]);
  const [history, setHistory] = useState<Rental[]>([]);
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [progressMap, setProgressMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    if (!loggedIn) return;
    setLoading(true);
    Promise.all([clientActiveRentals(), clientRentalHistory(), clientFavorites(), clientProgressMap()]).then(
      ([a, h, f, p]) => {
        setActive(a);
        setHistory(h);
        setFavorites(f);
        setProgressMap(p);
        setLoading(false);
      },
    );
  }, [loggedIn]);

  useEffect(() => { reload(); }, [reload]);

  if (!hydrating && (!loggedIn || !user)) {
    return (
      <div className="page-fade">
        <Header current="shelf" />
        <Section pad="xl">
          <Container>
            <EmptyState
              title="Tu estantería te espera"
              body="Inicia sesión para guardar tus lecturas y gestionar tus préstamos."
              action={
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                  <Link href="/iniciar-sesion" className="btn btn-primary">Iniciar sesión</Link>
                  <Link href="/crear-cuenta" className="btn btn-ghost">Crear cuenta</Link>
                </div>
              }
            />
          </Container>
        </Section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="page-fade">
      <Header current="shelf" />

      <Section pad="lg">
        <Container>
          <div>
            <div className="eyebrow" style={{ marginBottom: 14 }}>Tu cuenta</div>
            <h1 className="serif" style={{ margin: 0, fontSize: 52, lineHeight: 1.02, letterSpacing: '-0.02em', fontWeight: 600 }}>
              Mi estantería
            </h1>
            <p style={{ marginTop: 16, color: 'var(--ink-soft)', fontSize: 16, maxWidth: 520 }}>
              Aquí están tus préstamos activos, lo que ya leíste y los títulos que has guardado.
              {user ? ` Buen camino, ${user.name}.` : ''}
            </p>
          </div>
        </Container>
      </Section>

      <hr className="rule-strong" />

      <Section pad="md">
        <Container>
          <div className="tabbar" role="tablist" style={{ marginBottom: 16 }}>
            {(
              [
                ['activos', `Préstamos activos · ${active.length}`],
                ['historial', `Historial · ${history.length}`],
                ['favoritos', `Favoritos · ${favorites.length}`],
              ] as const
            ).map(([k, l]) => (
              <button key={k} className="tab" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
                {l}
              </button>
            ))}
          </div>

          <div style={{ paddingTop: 16 }}>
            {loading && <div style={{ color: 'var(--stone)', fontSize: 14, padding: '24px 0' }}>Cargando…</div>}

            {!loading && tab === 'activos' && (
              <>
                {active.map((l) => (
                  <LoanCard key={l.id} loan={l} progress={progressMap[l.bookId]} onReturned={reload} />
                ))}
                {active.length < 2 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 28, padding: '28px 0', color: 'var(--stone)' }}>
                    <div
                      style={{
                        width: 120,
                        aspectRatio: '2/3',
                        border: '1px dashed var(--rule-2)',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        color: 'var(--stone)',
                      }}
                    >
                      Disponible
                    </div>
                    <div style={{ alignSelf: 'center' }}>
                      <div className="serif" style={{ fontSize: 22 }}>Un espacio libre</div>
                      <div style={{ marginTop: 6, fontSize: 14 }}>
                        Puedes tomar prestado un título más.{' '}
                        <Link href="/" className="under" style={{ color: 'var(--ink)' }}>
                          Buscar en el catálogo →
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {!loading && tab === 'historial' && (
              <div>
                {history.length === 0 && (
                  <div style={{ color: 'var(--stone)', fontSize: 14, padding: '24px 0' }}>
                    Aún no has devuelto ningún título.
                  </div>
                )}
                {history.map((h) => (
                  <HistoryRow key={h.id} entry={h} />
                ))}
              </div>
            )}

            {!loading && tab === 'favoritos' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 168px)', gap: 36, paddingTop: 8 }} className="m-book-grid">
                {favorites.length === 0 && (
                  <div style={{ color: 'var(--stone)', fontSize: 14, gridColumn: '1 / -1' }}>
                    Guarda títulos tocando el corazón en la página de cada libro.
                  </div>
                )}
                {favorites.map((f) => (
                  <BookCard key={f.bookId} book={favoriteToBook(f)} width={168} />
                ))}
              </div>
            )}
          </div>
        </Container>
      </Section>

      <Footer />
    </div>
  );
}
